export type FeedbackResultKind = 'success' | 'info' | 'warning' | 'error' | 'loading';

export interface FeedbackResultCopy {
	title: string;
	detail: string;
}

export interface FeedbackResultPresentation extends FeedbackResultCopy {
	kind: FeedbackResultKind;
	token: '--success' | '--info' | '--warning' | '--error';
	ariaLabel: string;
}

const DEFAULT_COPY: Record<FeedbackResultKind, FeedbackResultCopy> = {
	success: { title: 'Success', detail: '' },
	info: { title: 'Heads up', detail: '' },
	warning: { title: 'Heads up', detail: '' },
	error: { title: 'Something went wrong', detail: '' },
	loading: { title: 'Loading', detail: '' },
};

const PRESENTATIONS: Record<FeedbackResultKind, Omit<FeedbackResultPresentation, 'title' | 'detail'>> = {
	success: { kind: 'success', token: '--success', ariaLabel: 'Success' },
	info: { kind: 'info', token: '--info', ariaLabel: 'Information' },
	warning: { kind: 'warning', token: '--warning', ariaLabel: 'Warning' },
	error: { kind: 'error', token: '--error', ariaLabel: 'Error' },
	loading: { kind: 'loading', token: '--info', ariaLabel: 'Loading' },
};

const ICON_PATHS: Record<FeedbackResultKind, string> = {
	success: '<circle cx="12" cy="12" r="9"></circle><path d="m8 12 2.6 2.6L16.5 9"></path>',
	info: '<circle cx="12" cy="12" r="9"></circle><path d="M12 11v5"></path><path d="M12 8h.01"></path>',
	warning: '<path d="m10.3 3.7-8 13.8A2 2 0 0 0 4 20.5h16a2 2 0 0 0 1.7-3l-8-13.8a2 2 0 0 0-3.4 0Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path>',
	error: '<circle cx="12" cy="12" r="9"></circle><path d="m9 9 6 6"></path><path d="m15 9-6 6"></path>',
	loading: '<circle cx="12" cy="12" r="8"></circle><path d="M12 4a8 8 0 0 1 8 8"></path>',
};

/** One semantic result value always owns its icon, token, announced state, and copy. */
export function feedbackResult(kind: FeedbackResultKind, copy: Partial<FeedbackResultCopy> = {}): FeedbackResultPresentation {
	return { ...PRESENTATIONS[kind], ...DEFAULT_COPY[kind], ...copy };
}

/** Decorative SVG only; the containing result surface owns the accessible label. */
export function feedbackResultIcon(kind: FeedbackResultKind): string {
	const spinnerClass = kind === 'loading' ? ' class="feedback-result-spinner"' : '';
	return `<svg${spinnerClass} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON_PATHS[kind]}</svg>`;
}
